import { prefersReducedMotion } from './motion';

interface ViewTransitionDocument {
  startViewTransition?: (update: () => void) => { finished: Promise<unknown> };
}

/**
 * Runs a theme change as a circular reveal that grows from `origin` (the
 * center of the toggle button) over the old theme, using the View Transitions
 * API. The animation itself is CSS (`::view-transition-new(root)` in
 * kit-next.css, keyed on `data-theme-swap`); this only publishes the circle and
 * lets the browser snapshot. Without the API, or with reduced motion, the
 * change is applied directly.
 *
 * `update` must change the DOM synchronously (the caller wraps its state
 * change in `flushSync`), because the browser takes the "after" snapshot as
 * soon as the callback returns.
 */
export function runThemeTransition(update: () => void, origin?: { x: number; y: number }) {
  const doc = document as Document & ViewTransitionDocument;
  if (!doc.startViewTransition || prefersReducedMotion()) {
    update();
    return;
  }
  const root = document.documentElement;
  const x = origin?.x ?? window.innerWidth / 2;
  const y = origin?.y ?? window.innerHeight / 2;
  // Radius that reaches the farthest corner, so the circle always covers the screen.
  const radius = Math.hypot(Math.max(x, window.innerWidth - x), Math.max(y, window.innerHeight - y));
  root.style.setProperty('--vt-x', `${x}px`);
  root.style.setProperty('--vt-y', `${y}px`);
  root.style.setProperty('--vt-r', `${radius}px`);
  root.setAttribute('data-theme-swap', '');
  const clear = () => {
    root.removeAttribute('data-theme-swap');
    root.style.removeProperty('--vt-x');
    root.style.removeProperty('--vt-y');
    root.style.removeProperty('--vt-r');
  };
  doc.startViewTransition(update).finished.then(clear, clear);
}
